import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Crown, FileText, ExternalLink, CreditCard, XCircle, ArrowRight, AlertCircle, RefreshCw, Download } from 'lucide-react';
import { useBillingCurrency } from '../../hooks/useBillingCurrency';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const downloadInvoice = async (invoice) => {
  // Stripe-native invoices expose a direct, signed PDF URL — open it directly
  // (they are NOT in our local subscription_invoices table, so the local PDF
  // endpoint would 404). This fixes the "Not authenticated" error on Stripe invoices.
  if (invoice.invoice_pdf) {
    window.open(invoice.invoice_pdf, '_blank', 'noopener');
    return;
  }
  if (invoice.hosted_invoice_url) {
    window.open(invoice.hosted_invoice_url, '_blank', 'noopener');
    return;
  }
  // Local (LPL / GST) invoices: authenticated blob download from our API.
  // Use the UUID id (not invoice_number — its slashes break URL path routing → 404).
  const ref = invoice.id || invoice.invoice_number;
  const safe = (invoice.invoice_number || ref || 'invoice').replace(/[/\s]/g, '-');
  const filename = `Invoice-${safe}.pdf`;
  const token = localStorage.getItem('access_token');
  try {
    const r = await fetch(`${API_BASE}/api/subscriptions/invoices/${encodeURIComponent(ref)}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error(`status ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;          // descriptive saved filename, not a random blob hash
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error('Could not download this invoice. Please try again or contact support.');
  }
};

// Data-aware amount formatter: render each transaction in ITS OWN currency.
// Razorpay records carry native INR (amount_inr / currency='INR'); Stripe records
// carry USD (amount/100 -> *_usd). Never blindly ×rate (would double-convert).
const fmtTxnAmount = (rec) => {
  if (!rec) return '$0.00';
  if (rec.currency === 'INR' || rec.amount_inr != null || rec.amount_paid_inr != null) {
    const inr = rec.amount_inr ?? rec.amount_paid_inr ?? rec.amount_due_inr ?? rec.amount ?? 0;
    return `₹${Number(inr).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const usd = rec.amount_paid_usd ?? rec.amount_paid ?? rec.amount_due_usd ?? rec.amount_due ?? rec.amount_usd ?? rec.amount ?? 0;
  return `$${Number(usd).toFixed(2)}`;
};

const SectionLoadError = ({ onRetry }) => (
  <Card className="border-orange-200 bg-orange-50" data-testid="billing-section-error">
    <CardContent className="p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-sm text-orange-800">
        <AlertCircle className="h-4 w-4 shrink-0" /> Could not load this section right now.
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} data-testid="billing-section-retry">
        <RefreshCw className="h-4 w-4 mr-1" /> Retry
      </Button>
    </CardContent>
  </Card>
);

const CurrentPlanBanner = ({ currentPlan, billing }) => {
  const bc = useBillingCurrency();
  if (!currentPlan || currentPlan.plan_id === 'free') return null;
  const priceLabel = bc.currency === 'INR' && currentPlan.monthly_price_inr != null
    ? `₹${Number(currentPlan.monthly_price_inr).toLocaleString('en-IN')}`
    : `$${currentPlan.monthly_price_usd?.toFixed(2)}`;
  return (
    <Card data-testid="current-plan-banner" className="border-green-500/30 bg-green-500/5">
      <CardContent className="py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500/15 flex items-center justify-center">
              <Crown className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="font-semibold text-green-400">{currentPlan.name} Plan</p>
              <p className="text-sm text-muted-foreground">Active subscription &bull; Next billing cycle</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-400">{priceLabel}<span className="text-sm font-normal text-muted-foreground">/mo{bc.currency === 'INR' ? ' + GST' : ''}</span></p>
            <Badge variant="default" className="text-xs bg-green-600">active</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const BillingInvoicesTab = ({ subscriptionInvoices, invoices, currentPlan, billing, gateway = 'stripe', loadError, onRetry }) => {
  return (
    <div className="space-y-6">
      {loadError && <SectionLoadError onRetry={onRetry} />}
      <CurrentPlanBanner currentPlan={currentPlan} billing={billing} />

      {/* Subscription Invoices */}
      <Card data-testid="subscription-invoices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-400" />Subscription Invoices
          </CardTitle>
          <CardDescription>Invoices for your subscription payments</CardDescription>
        </CardHeader>
        <CardContent>
          {subscriptionInvoices.length > 0 ? (
            <div className="space-y-3">
              {subscriptionInvoices.map((invoice, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium">{invoice.invoice_number}</p>
                      <p className="text-sm text-muted-foreground">{invoice.plan_name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(invoice.invoice_date).toLocaleDateString()} &bull; {invoice.payment_gateway?.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{invoice.currency} {invoice.amount?.toFixed(2) || '0.00'}</p>
                      <Badge variant="default" className="text-xs bg-green-500">{invoice.status}</Badge>
                    </div>
                    {invoice.hosted_invoice_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => downloadInvoice(invoice)} data-testid={`download-invoice-${idx}`} title="Download invoice PDF">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Crown className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No subscription invoices yet</p>
              <p className="text-sm">Invoices will appear here after subscription purchase</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe Invoice History — only relevant for USD/Stripe tenants */}
      {gateway === 'stripe' && (
      <Card data-testid="invoices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-400" />Invoice History
          </CardTitle>
          <CardDescription>Your past invoices from Stripe</CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="space-y-3">
              {invoices.map((invoice, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-4">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{invoice.number || invoice.stripe_invoice_id}</p>
                      <p className="text-sm text-muted-foreground">{invoice.period_start?.split('T')[0]} - {invoice.period_end?.split('T')[0]}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{fmtTxnAmount(invoice)}</p>
                      <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className="text-xs">{invoice.status}</Badge>
                    </div>
                    {invoice.hosted_invoice_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-sm">Invoices will appear here after your first payment</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export const BillingPaymentsTab = ({ payments, currentPlan, billing, loadError, onRetry }) => {
  return (
    <div className="space-y-6">
      {loadError && <SectionLoadError onRetry={onRetry} />}
      <CurrentPlanBanner currentPlan={currentPlan} billing={billing} />

      <Card data-testid="payments-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-400" />Payment History
          </CardTitle>
          <CardDescription>Your past payments</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length > 0 ? (
            <div className="space-y-3">
              {payments.map((payment, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-4">
                    {payment.status === 'succeeded' ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
                    <div>
                      <p className="font-medium">{payment.description || 'Payment'}</p>
                      <p className="text-sm text-muted-foreground">{payment.created_at?.split('T')[0]} &bull; {payment.payment_method || 'card'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{fmtTxnAmount(payment)}</p>
                    <Badge variant={payment.status === 'succeeded' ? 'default' : 'destructive'} className="text-xs">{payment.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No payments yet</p>
              <p className="text-sm">Payments will appear here after your first transaction</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
